import { Box, IconButton, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useStyles } from "../../../MachineCenterCreationForm.styles";
import { ProductListProps, T_PREFIX } from "./interfaces";
import { useProductList } from "./useProductList";

export const ProductList = (props: ProductListProps) => {
    const classes = useStyles();
    const { disabled, t } = props;
    const {
        valueLabel,
        nameInputRef,
        nameInput,
        valueInput,
        duplicateNameError,
        editingKey,
        editName,
        editValue,
        duplicateNameEditError,
        activeProducts,
        canAdd,
        handleNameInputChange,
        handleValueInputChange,
        handleAdd,
        handleDelete,
        handleStartEdit,
        handleEditNameChange,
        handleEditValueChange,
        handleConfirmEdit,
        handleCancelEdit,
        getProductKey,
        getCurrentIdealValue,
    } = useProductList(props);

    return (
        <>
            <Box className={classes.productInputRow}>
                <TextField
                    className={classes.textField}
                    placeholder={t(`${T_PREFIX}.oee_product_name`)}
                    variant="outlined"
                    value={nameInput}
                    onChange={(e) => handleNameInputChange(e.target.value)}
                    autoFocus
                    inputRef={nameInputRef}
                    error={duplicateNameError}
                    helperText={duplicateNameError ? t(`${T_PREFIX}.oee_product_duplicate_name`) : undefined}
                    disabled={disabled}
                    inputProps={{ maxLength: 150 }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleAdd();
                        }
                    }}
                    sx={{ flex: 2 }}
                />
                <TextField
                    className={classes.textField}
                    placeholder={valueLabel}
                    variant="outlined"
                    type="text"
                    value={valueInput}
                    onChange={(e) => handleValueInputChange(e.target.value)}
                    disabled={disabled}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleAdd();
                        }
                    }}
                    sx={{ flex: 1 }}
                    InputProps={{
                        endAdornment: (
                            <IconButton onClick={handleAdd} disabled={disabled || !canAdd}>
                                <AddIcon />
                            </IconButton>
                        ),
                    }}
                />
            </Box>
            <Box className={classes.productOutputContainer}>
                {activeProducts.map((product) => {
                    const key = getProductKey(product);
                    const isEditing = editingKey === key;
                    const idealValue = getCurrentIdealValue(product);

                    return isEditing ? (
                        <Box key={key} className={classes.productInputRow}>
                            <TextField
                                variant="outlined"
                                placeholder={t(`${T_PREFIX}.oee_product_name`)}
                                value={editName}
                                onChange={(e) => handleEditNameChange(e.target.value)}
                                autoFocus
                                error={duplicateNameEditError}
                                helperText={
                                    duplicateNameEditError ? t(`${T_PREFIX}.oee_product_duplicate_name`) : undefined
                                }
                                inputProps={{ maxLength: 150 }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleConfirmEdit();
                                    }
                                }}
                                sx={{ flex: 2 }}
                            />
                            <TextField
                                variant="outlined"
                                placeholder={valueLabel}
                                type="text"
                                value={editValue}
                                onChange={(e) => handleEditValueChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleConfirmEdit();
                                    }
                                }}
                                sx={{ flex: 1 }}
                                InputProps={{
                                    endAdornment: (
                                        <>
                                            <IconButton onClick={handleConfirmEdit} size="small">
                                                <CheckIcon />
                                            </IconButton>
                                            <IconButton onClick={handleCancelEdit} size="small">
                                                <CloseIcon />
                                            </IconButton>
                                        </>
                                    ),
                                }}
                            />
                        </Box>
                    ) : (
                        <Box key={key} className={classes.productInputRow} sx={{ alignItems: "center" }}>
                            <Typography
                                variant="body2"
                                sx={{ flex: 2, fontFamily: "Open Sans", fontSize: "14px", paddingLeft: "14px" }}
                            >
                                {product.name}
                            </Typography>
                            <Box sx={{ flex: 1, display: "flex", alignItems: "center", paddingLeft: "14px" }}>
                                <Typography variant="body2" sx={{ flex: 1, fontFamily: "Open Sans", fontSize: "14px" }}>
                                    {idealValue}
                                </Typography>
                                <IconButton onClick={() => handleStartEdit(product)} disabled={disabled} size="small">
                                    <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton onClick={() => handleDelete(product)} disabled={disabled} size="small">
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </>
    );
};
